import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { claimHistorySession, endHistorySession, startHistorySession } from '@/historySession';
import { useBranchesStore } from '@/shared/store/branchesStore';

// The ERP side of a signed-in session (D1): the tab's history session
// (historySession.ts, owned by a user and company) plus the per-session
// stores that must not carry over to the next user or company. The branch
// store is reset so the next session's lists wait for their own branches
// instead of sending the previous one's X-Branch-Id.
export const sessionOwner = (userId: string, companyId: string): string => `${userId}:${companyId}`;

export function startErpSession(owner: string): void {
  startHistorySession(owner);
  useBranchesStore.getState().reset();
}

export function endErpSession(): void {
  endHistorySession();
  useBranchesStore.getState().reset();
}

// Set when /auth/me finds the tab's session belonged to someone else: the
// page the tab booted at is theirs, so Protected sends the router home once.
export const useBootResetStore = create<{ pending: boolean }>(() => ({ pending: false }));

export function claimErpSession(owner: string): void {
  if (!claimHistorySession(owner)) return;
  useBranchesStore.getState().reset();
  useBootResetStore.setState({ pending: true });
}

// True while a dropped boot page is still on screen (render <Navigate to="/"
// replace /> then); cleared once the router is home.
export function useBootReset(): boolean {
  const pending = useBootResetStore((s) => s.pending);
  const location = useLocation();
  const atHome = location.pathname === '/' && !location.search;
  useEffect(() => {
    if (pending && atHome) useBootResetStore.setState({ pending: false });
  }, [pending, atHome]);
  return pending && !atHome;
}
